import { useCallback, useMemo } from 'react'
import { EProgramTypes } from '@ucheba/utils/types/program'
import { subjectsToSetValFormat } from '@ucheba/utils/helpers/subjects'
import {
  getFullRouterPath,
  getQueryEgeList,
  objectToQueryString,
  redirect,
} from '@ucheba/utils/helpers/router'
import { getCountForm } from '@ucheba/utils/helpers/number'
import { useRouter } from 'next/router'
import { useDispatch, useSelector } from 'react-redux'
import { institutionsSelectors, institutionsThunks } from '@ucheba/store/institutions'
import { specialitiesIdSelectors } from '@ucheba/store/specialities/id'
import {
  specialitiesIdSimilarSelectors,
  specialitiesIdSimilarThunks,
} from '@ucheba/store/specialities/id/similar'
import { coreQuery } from '@ucheba/utils/constants/core'
import { authSelectors } from '@ucheba/store/auth'
import { IUseSpecialityIdPageCore } from './types'

export const getIsForInstitution = (specialityType: number): boolean => {
  return (
    specialityType === EProgramTypes.higherBachelor ||
    specialityType === EProgramTypes.higherSpeciality
  )
}

export const useSpecialityIdPageCore: IUseSpecialityIdPageCore = ({
  trainingFormTypes,
  specialization,
  specialityType,
}) => {
  const { query, asPath } = useRouter()

  const isAuth = useSelector(authSelectors.isAuth)

  const dispatch = useDispatch()

  const entity = useSelector(specialitiesIdSelectors.entity)

  const totalInstitutions = useSelector(institutionsSelectors.total)
  const totalSimilar = useSelector(specialitiesIdSimilarSelectors.total)

  const trainingForms = useMemo(
    () =>
      trainingFormTypes
        .map(({ name: trainingFormName }, index) =>
          !index ? `Формы обучения: ${trainingFormName}` : trainingFormName
        )
        .join(', '),
    [trainingFormTypes]
  )

  /** Специальность для вуза */
  const isForInstitution = useMemo(
    () => getIsForInstitution(specialityType.value),
    [specialityType.value]
  )

  const specializationData = useMemo(() => {
    const specializationString = specialization
      ? specialization.toLocaleLowerCase().replace(/;/g, ',').replace(/\.$/g, '')
      : ''

    if (!specializationString) return ''

    const title = isForInstitution ? 'Профили обучения: ' : 'Обучение: '

    return title + specializationString
  }, [isForInstitution, specialization])

  const getLinkProps = useCallback(
    ({ id, subjects, institutionsCount }) => {
      const preparedSubjects = subjectsToSetValFormat(subjects)
      const querySubjects = getQueryEgeList(preparedSubjects)

      return {
        href: `/for-abiturients/${
          isForInstitution ? 'vuz' : 'college'
        }?eq[]=__l:9653&eq[]=__sp:${id}&o=point&d=desc&ege=1&${querySubjects}&ege_add_exam=1`,
        name: `${institutionsCount} ${getCountForm(
          institutionsCount,
          isForInstitution
            ? ['вуз', 'вуза', 'вузов']
            : ['колледж', 'колледжа', 'колледжей']
        )}`,
      }
    },
    [isForInstitution]
  )

  const institutionSearchLink = useMemo(
    () =>
      `/for-abiturients/${isForInstitution ? 'vuz' : 'college'}?eq[]=__l:9653&eq[]=__sp:${
        query.id
      }`,
    [isForInstitution, query.id]
  )

  const isCollage = useMemo(() => {
    if (specialityType) {
      return (
        specialityType.value === EProgramTypes.vocational ||
        specialityType.value === EProgramTypes.vocationalSpo ||
        specialityType.value === EProgramTypes.vocationalNpo
      )
    }

    return false
  }, [specialityType])

  const programTypes = getIsForInstitution(entity.specialityType.value)
    ? [EProgramTypes.higherBachelor, EProgramTypes.higherSpeciality]
    : [EProgramTypes.vocationalNpo, EProgramTypes.vocationalSpo]

  const onClickGetMoreInstitutions = useCallback(() => {
    dispatch(
      institutionsThunks.fetch({
        data: { specialityIds: [query.id], programTypes, limit: 10 },
      })
    )
  }, [dispatch, programTypes, query.id])

  const hrefBySpecialty = useMemo(() => {
    if (isForInstitution) {
      return `/for-abiturients/vuz/spec/${query.id}`
    }
    return `/for-abiturients/college/spec/${query.id}`
  }, [isForInstitution, query.id])

  const onClickGetMoreSimilar = useCallback(() => {
    dispatch(
      specialitiesIdSimilarThunks.fetch({
        data: { id: query.id, programTypes, limit: 20 },
      })
    )
  }, [dispatch, programTypes, query.id])

  return {
    trainingForms,
    specializationData,
    isForInstitution,
    getLinkProps,
    institutionSearchLink,
    isCollage,
    onClickGetMoreInstitutions,
    totalInstitutions,
    totalSimilar,
    onClickGetMoreSimilar,
    hrefBySpecialty,
  }
}

// Получаем строку с информацией о специальности - направление, код, профиль, направление
export const useSpecialityHeaderInfo = (data: any): string => {
  const { speciality, specialityType, codeOkso } = data
  const specialitet = 'специалитет'
  const name = specialityType.name[0].toUpperCase() + specialityType.name.slice(1)

  // Если специальность профиля специалитета
  if (specialityType.name.toLowerCase() === specialitet) {
    return speciality
      ? `${name}, специальность "${speciality.name}"`
      : `${name}, код специальности ${codeOkso}`
  }

  // Если специальность профиля бакалавриата
  return speciality
    ? `${name}, профиль направления "${speciality.name}"`
    : `${name}, код направления ${codeOkso}`
}
